import { Controller } from "stimulus";
import { logEvent } from "utils/analytics";

function generateAttemptId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `attempt-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default class ConnectLink extends Controller {
  connect() {
    ({ player: this.player, source: this.source, channel: this.channel } = this.element.dataset);

    const deepLink = this.element.dataset.deepLink;
    if (deepLink) setTimeout(() => window.open(deepLink, this.element.dataset.targetWindow || "_self"), 1);
    this.element.querySelectorAll(".player-link").forEach(el => el.addEventListener("click", this.reset.bind(this)));

    const status = this.element.dataset.defaultStatus || "loading";
    // Landing on an already-connected page is not a connection attempt: don't start one or log a click.
    // "idle" waits for the user to click the Open button (reset) before polling.
    if (status === "done" || status === "idle") {
      this.setStatus(status);
    } else {
      this.startAttempt();
      this.setStatus(status);
    }
  }

  disconnect() {
    window.clearInterval(this.pollingInterval);
    window.clearTimeout(this.timeoutInterval);
  }

  reset() {
    this.startAttempt();
    this.setStatus("loading");
  }

  // A new attempt: fresh id, cleared terminal flags, and a Clicked event at the top of the funnel.
  startAttempt() {
    this.attemptId = generateAttemptId();
    this.connectedEventFired = false;
    this.timedOutEventFired = false;
    this.track("Player Connect - Clicked");
  }

  eventProps(extra = {}) {
    const props = { attempt_id: this.attemptId, ...extra };
    if (this.player) props.player = this.player;
    if (this.source) props.source = this.source;
    if (this.channel) props.channel = this.channel;
    return props;
  }

  track(name, extra) {
    logEvent(name, this.eventProps(extra));
  }

  statusUrl() {
    const url = new URL(this.element.dataset.statusUrl, window.location.origin);
    url.searchParams.set("attempt_id", this.attemptId);
    return url.toString();
  }

  async checkStatus() {
    const response = await fetch(this.statusUrl());
    const data = await response.json();
    if (data.status == "connected") {
      // Exactly one terminal event per attempt: skip if this attempt already timed out or connected.
      if (!this.connectedEventFired && !this.timedOutEventFired) {
        this.connectedEventFired = true;
        const extra = {};
        if (data.time_to_connect_ms !== undefined) extra.time_to_connect_ms = data.time_to_connect_ms;
        this.track("Feed Connected", extra);
      }
      this.setStatus("done");
    }
  }

  timeout() {
    if (!this.timedOutEventFired && !this.connectedEventFired) {
      this.timedOutEventFired = true;
      this.track("Player Connect - Timed Out");
    }
    this.setStatus("failed");
  }

  setStatus(status) {
    if (this.pollingInterval) window.clearInterval(this.pollingInterval);
    if (this.timeoutInterval) window.clearTimeout(this.timeoutInterval);

    if (status === "loading") {
        this.pollingInterval = window.setInterval(this.checkStatus.bind(this), 1000);
        this.timeoutInterval = window.setTimeout(this.timeout.bind(this), 20000);
    } else if (status === "failed") {
        this.pollingInterval = window.setInterval(this.checkStatus.bind(this), 5000);
    }

    this.element.querySelectorAll('[data-visible]').forEach(el => {
        if (el.dataset.visible.includes(status)) {
            el.classList.remove("hidden");
        } else {
            el.classList.add("hidden");
        }
    });
  }
}
